import {useState} from "react";
import {useHistory} from "react-router-dom";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import {
    Alert,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField
} from "@mui/material";
import BigSelector, {BigSelectorItem} from "../../components/common/BigSelector/BigSelector";
import {useAdminApi, ADMIN_USERS_KEY, ADMIN_ADD_USER_OPTIONS_KEY} from "./api";

type AddUserDialogProps = {
    open: boolean;
    onClose: () => void;
};

function AddUserDialog({open, onClose}: AddUserDialogProps) {
    var api = useAdminApi();
    var queryClient = useQueryClient();

    var [email, setEmail] = useState("");
    var [password, setPassword] = useState("");
    var [selectedAccount, setSelectedAccount] = useState("");
    var [selectedRoles, setSelectedRoles] = useState<string[]>([]);
    var [saving, setSaving] = useState(false);
    var [error, setError] = useState<string | null>(null);

    var {data: options, isLoading: loadingOptions} = useQuery(
        [ADMIN_ADD_USER_OPTIONS_KEY],
        () => api.getAddUserOptions(),
        {enabled: open, staleTime: 60000}
    );

    function resetForm() {
        setEmail("");
        setPassword("");
        setSelectedAccount("");
        setSelectedRoles([]);
        setError(null);
    }

    function handleClose() {
        resetForm();
        onClose();
    }

    async function handleSubmit() {
        if (!email || !password || !selectedAccount) {
            setError("Please fill in all required fields");
            return;
        }

        if (password.length < 8) {
            setError("Password must be at least 8 characters");
            return;
        }

        var account = options?.accounts.find(a => a.accountKey === selectedAccount);
        if (!account) {
            setError("Please select an account");
            return;
        }

        try {
            setSaving(true);
            setError(null);
            await api.addUser(email, password, account.namespace, account.accountKey, selectedRoles);
            await queryClient.invalidateQueries([ADMIN_USERS_KEY]);
            handleClose();
        } catch (e: any) {
            setError(e.message || "Failed to create user");
        } finally {
            setSaving(false);
        }
    }

    if (saving) {
        return (
            <Dialog open={open} maxWidth="sm" fullWidth>
                <DialogTitle>Creating User...</DialogTitle>
                <DialogContent sx={{display: "flex", justifyContent: "center", padding: 4}}>
                    <CircularProgress/>
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>Add New User</DialogTitle>
            <DialogContent>
                {error && <Alert severity="error" sx={{mb: 2}}>{error}</Alert>}

                <TextField
                    autoFocus
                    margin="dense"
                    label="Email"
                    type="email"
                    fullWidth
                    variant="outlined"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    sx={{mb: 2}}
                />

                <TextField
                    margin="dense"
                    label="Password"
                    type="password"
                    fullWidth
                    variant="outlined"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    helperText="Minimum 8 characters"
                    sx={{mb: 2}}
                />

                <FormControl fullWidth sx={{mb: 2}}>
                    <InputLabel>Account</InputLabel>
                    <Select
                        value={selectedAccount}
                        label="Account"
                        onChange={(e) => setSelectedAccount(e.target.value)}
                        disabled={loadingOptions}
                    >
                        {options?.accounts.map((account) => (
                            <MenuItem key={account.accountKey} value={account.accountKey}>
                                {account.namespace} / {account.accountKey}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl fullWidth>
                    <InputLabel>Roles</InputLabel>
                    <Select
                        multiple
                        value={selectedRoles}
                        label="Roles"
                        onChange={(e) => setSelectedRoles(e.target.value as string[])}
                        disabled={loadingOptions}
                        renderValue={(selected) => (
                            <div style={{display: "flex", flexWrap: "wrap", gap: 4}}>
                                {selected.map((value) => (
                                    <Chip key={value} label={value} size="small"/>
                                ))}
                            </div>
                        )}
                    >
                        {options?.roles.sort().map((role) => (
                            <MenuItem key={role} value={role}>{role}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Cancel</Button>
                <Button onClick={handleSubmit} variant="contained" disabled={loadingOptions}>
                    Create User
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export function AdminUsersTab() {
    var history = useHistory();
    var [addDialogOpen, setAddDialogOpen] = useState(false);

    var api = useAdminApi();
    var {data: users, isLoading, isError, error} = useQuery(
        [ADMIN_USERS_KEY],
        () => api.listUsers(),
        {staleTime: 30000}
    );

    if (isError) {
        console.error(error);
        return <BigSelector title="Users" emptyMessage={<Alert severity="error">Failed to load users</Alert>}/>;
    }

    var items: BigSelectorItem[] = (users || []).map((user) => {
        var roleTags = (user.roles || []).map((role) => (
            <Chip key={role} label={role} size="small" color="primary" variant="outlined"/>
        ));

        var nameDisplay = (
            <div key={`name-${user.id}`}>
                <div style={{fontWeight: "bold"}}>{user.email}</div>
                {(user.firstName || user.lastName) && (
                    <div style={{fontSize: "0.85em", color: "#666"}}>
                        {user.firstName} {user.lastName}
                    </div>
                )}
            </div>
        );

        return {
            id: user.id.toString(),
            name: nameDisplay,
            subtitle: `${user.namespace} / ${user.accountKey}`,
            icon: <i className="fas fa-user"/>,
            inlineComponents: roleTags.length > 0 ? roleTags : undefined,
            onActivate: () => history.push(`/admin/users/${user.id}`),
        };
    });

    return (
        <>
            <BigSelector
                title="Users"
                items={items}
                loading={isLoading}
                emptyMessage="No users found"
                actions={{
                    icon: "fas fa-plus",
                    onClick: () => setAddDialogOpen(true),
                }}
            />
            <AddUserDialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)}/>
        </>
    );
}
